import React, { useState, useEffect, useRef } from 'react';
import { Loader2, X, Mail, Bold, Italic, Underline, List, Code } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable modal to compose & send emails (with subject + body editable).
 * Uses a WYSIWYG contentEditable area so non-technical users can edit without
 * touching raw HTML.
 */
export default function EmailEditModal({
  open,
  onClose,
  onSent,
  title = 'Enviar correo',
  sendEndpoint,
  initialTo = '',
  extraPayload = {},
  confirmLabel = 'Enviar',
  helperText = 'Puedes editar el texto como en Word. Tu firma y los datos automáticos se mantienen al final.',
}) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [toEmail, setToEmail] = useState(initialTo);
  const [cc, setCc] = useState([]);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const editorRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!open || !sendEndpoint) return;
    setToEmail(initialTo);
    setSubject('');
    setBodyHtml('');
    setShowCode(false);
    initializedRef.current = false;
    (async () => {
      setLoadingPreview(true);
      try {
        const r = await fetch(`${API}${sendEndpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...extraPayload, override_email: initialTo, preview_only: true }),
        });
        const data = await r.json();
        if (data.success && data.preview_only) {
          setSubject(data.subject || '');
          setBodyHtml(data.body_html || '');
          setCc(data.cc || []);
          if (data.to) setToEmail(data.to);
        } else {
          toast.error(data.error || data.detail || 'No se pudo generar la previsualización');
        }
      } catch (e) {
        toast.error('Error: ' + e.message);
      } finally {
        setLoadingPreview(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sendEndpoint, initialTo]);

  // Inject the HTML into the contentEditable div (only on first load or when switching code↔wysiwyg)
  useEffect(() => {
    if (!open || loadingPreview || showCode) return;
    if (editorRef.current && !initializedRef.current && bodyHtml) {
      editorRef.current.innerHTML = bodyHtml;
      initializedRef.current = true;
    }
  }, [open, loadingPreview, showCode, bodyHtml]);

  const exec = (cmd, value = null) => {
    // Keep the cursor in the editor
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(cmd, false, value);
    // Sync state
    if (editorRef.current) setBodyHtml(editorRef.current.innerHTML);
  };

  const onEditorInput = (e) => {
    setBodyHtml(e.currentTarget.innerHTML);
  };

  const toggleCodeView = () => {
    if (!showCode) {
      // wysiwyg → code: capture latest html from editor
      if (editorRef.current) setBodyHtml(editorRef.current.innerHTML);
    } else {
      // code → wysiwyg: reset initialized so it re-injects current html
      initializedRef.current = false;
    }
    setShowCode(!showCode);
  };

  const doSend = async () => {
    if (!toEmail || !toEmail.includes('@')) {
      toast.error('Correo destino inválido');
      return;
    }
    // Capture latest html from editor before sending
    const finalHtml = (!showCode && editorRef.current) ? editorRef.current.innerHTML : bodyHtml;
    setSending(true);
    try {
      const r = await fetch(`${API}${sendEndpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...extraPayload,
          override_email: toEmail,
          override_subject: subject,
          override_body_html: finalHtml,
        }),
      });
      const data = await r.json();
      if (data.success) {
        toast.success('Correo enviado');
        if (onSent) onSent(data);
        onClose();
      } else {
        toast.error(data.error || data.detail || 'Error al enviar');
      }
    } catch (e) {
      toast.error('Error: ' + e.message);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const ToolbarBtn = ({ onClick, title: t, children }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      title={t}
      className="p-1.5 rounded text-slate-300 hover:bg-slate-700 hover:text-white transition-colors">
      {children}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4"
         onClick={() => !sending && onClose()}>
      <div className="bg-slate-800 rounded-xl border border-slate-700 max-w-3xl w-full max-h-[90vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-emerald-400" />
            {title}
          </h3>
          <button onClick={onClose} disabled={sending} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loadingPreview ? (
          <div className="p-10 flex flex-col items-center gap-2 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm">Generando previsualización...</span>
          </div>
        ) : (
          <div className="p-5 space-y-4 overflow-y-auto flex-1">
            <p className="text-xs text-slate-400">{helperText}</p>

            <div>
              <label className="block text-xs text-slate-400 mb-1">Para:</label>
              <input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"
                data-testid="email-to-input" />
              {cc.length > 0 && <p className="text-xs text-slate-500 mt-1">CC: {cc.join(', ')}</p>}
            </div>

            <div>
              <label className="block text-xs text-slate-400 mb-1">Asunto:</label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"
                data-testid="email-subject-input" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs text-slate-400">Cuerpo del correo:</label>
                <button type="button" onClick={toggleCodeView}
                  className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
                  title="Ver código HTML (sólo para avanzados)">
                  <Code className="w-3 h-3" /> {showCode ? '◄ Volver al editor' : 'Ver HTML'}
                </button>
              </div>

              {!showCode && (
                <>
                  {/* Toolbar */}
                  <div className="flex items-center gap-1 bg-slate-900 border border-slate-600 border-b-0 rounded-t px-2 py-1">
                    <ToolbarBtn onClick={() => exec('bold')} title="Negrita (Ctrl+B)"><Bold className="w-4 h-4" /></ToolbarBtn>
                    <ToolbarBtn onClick={() => exec('italic')} title="Cursiva (Ctrl+I)"><Italic className="w-4 h-4" /></ToolbarBtn>
                    <ToolbarBtn onClick={() => exec('underline')} title="Subrayado"><Underline className="w-4 h-4" /></ToolbarBtn>
                    <span className="w-px h-5 bg-slate-700 mx-1"></span>
                    <ToolbarBtn onClick={() => exec('insertUnorderedList')} title="Lista"><List className="w-4 h-4" /></ToolbarBtn>
                    <ToolbarBtn onClick={() => exec('formatBlock', '<p>')} title="Párrafo">¶</ToolbarBtn>
                    <span className="w-px h-5 bg-slate-700 mx-1"></span>
                    <ToolbarBtn
                      onClick={() => {
                        const url = window.prompt('URL del enlace:', 'https://');
                        if (url) exec('createLink', url);
                      }}
                      title="Insertar enlace">🔗</ToolbarBtn>
                    <ToolbarBtn onClick={() => exec('removeFormat')} title="Quitar formato">🧹</ToolbarBtn>
                  </div>
                  {/* WYSIWYG Editor */}
                  <div
                    ref={editorRef}
                    contentEditable
                    onInput={onEditorInput}
                    className="w-full bg-white text-slate-900 rounded-b border border-slate-600 p-4 text-sm min-h-[280px] max-h-[400px] overflow-y-auto focus:border-emerald-500 focus:outline-none"
                    data-testid="email-body-wysiwyg"
                    suppressContentEditableWarning
                    style={{ lineHeight: '1.5' }}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Edita el texto como en un procesador de palabras. No toques la firma ni el logotipo del final — se mantienen intactos al enviar.
                  </p>
                </>
              )}

              {showCode && (
                <textarea
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  rows={14}
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white text-xs font-mono focus:border-emerald-500 focus:outline-none"
                  data-testid="email-body-input"
                />
              )}
            </div>
          </div>
        )}

        <div className="flex gap-3 p-4 border-t border-slate-700">
          <button onClick={onClose} disabled={sending}
            className="flex-1 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={doSend} disabled={sending || loadingPreview || !toEmail.includes('@')}
            className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="email-confirm-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
